import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  TooltipProps,
  Cell,
} from 'recharts';

import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { dummyData, exchangeRates, SAMPLE_LISTING_HISTORICAL_DATA, SCALE_CTA } from 'src/const';
import {
  COLOR_GREY_200,
  COLOR_PRIMARY_1_400,
  COLOR_PRIMARY_1_800,
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_2_300,
  COLOR_TEXT_2,
  KPI_CARD_COLOR,
} from 'src/colors';
import { User } from 'src/interfaces/iuser';
import { callApi } from 'src/core/callApi';
import {
  abbreviateNumber,
  checkAdrAnomaly,
  convertCurrency,
  formatCurrency,
} from 'src/utils/functions';
import UnavailableIcon from 'src/App/Components/Icons/UnavailableIcon';
import { InfoButton } from 'src/App/Components/Buttons';
import InfoTooltipContent from 'src/App/HOC/Tooltips/InfoTooltipContent';
import styles from './style';

// -------- Types (adjust or refine as you see fit) --------
interface HistoricalPerformanceGraphProps {
  listingID: string;
  handleOnSampleClick: (plan: string) => void;
  currency: string;
  setMarketUrlBack: () => void;
  triggerModalClose: any;
  isAdrAnomaly: boolean;
  showOccupancyGraphOnly: boolean;
  noTitle?: boolean;
  isDemo: boolean | null;
  setIsDemo: React.Dispatch<React.SetStateAction<boolean | null>>;
}

interface IDataPoint {
  month: string;
  LTMData: {
    occupancy_rate?: number | string;
    adr?: number | string;
    revenue?: number | string;
    active_days_count?: number | string;
    days_in_current_month?: number | string;
    inactivity_reason?: 'HO' | 'LO' | 'C60' | null;
  };
  last2YearsData: {
    occupancy_rate?: number | string;
    adr?: number | string;
    revenue?: number | string;
    active_days_count?: number | string;
  };
  last3YearsData: {
    occupancy_rate?: number | string;
    adr?: number | string;
    revenue?: number | string;
    active_days_count?: number | string;
  };
  latestYear: number;
  previousYear: number;
  twoYearsBefore: number;
  monthYear: string;
}

interface IListingHistoryResponse {
  message: any;
  firstAvailableMonth: string;
}

interface KPIGraphComponentProps {
  kpi: {
    key: string;
    label: string;
  };
  data: IDataPoint[] | null;
  LockedSampleGraph: JSX.Element;
  renderGraph: (kpiKey: string, selectedYears: string[], data: IDataPoint[]) => JSX.Element;
}

// ---------------------------------------------------------

// ---------------- CONST ------------------------------

const CURRENT_YEAR_KEY = 'currentYear';
const PREVIOUS_YEAR_KEY = 'previousYear';
const TOW_YEARS_AGO_KEY = '2YearsAgo';

const getMonthName = (dateString: string) => {
  const date = new Date(dateString + '-01');
  return date.toLocaleString('default', { month: 'short' });
};

const KPIGraphComponent = ({
  kpi,
  data,
  renderGraph,
  LockedSampleGraph,
}: KPIGraphComponentProps) => {
  const [selectedYears, setSelectedYears] = useState<string[]>([CURRENT_YEAR_KEY]);

  const handleCheckboxSelection = (event: any) => {
    const clickedOption = event.target.value;

    setSelectedYears(previousValues => {
      const newValues = [...previousValues];

      if (newValues.includes(clickedOption)) {
        newValues.splice(newValues.indexOf(clickedOption), 1);
      } else {
        newValues.push(clickedOption);
      }

      return newValues;
    });
  };

  return (
    <Grid container item xs={12}>
      <Box key={kpi.key} sx={{ mb: 4, width: '100%' }}>
        <Grid
          container
          item
          xs={12}
          justifyContent={'space-between'}
          alignItems={'center'}
          sx={{ mt: 2 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {kpi.label}
          </Typography>
          <FormGroup row onChange={event => handleCheckboxSelection(event)}>
            <FormControlLabel
              control={
                <Checkbox
                  value={CURRENT_YEAR_KEY}
                  checked={selectedYears.includes(CURRENT_YEAR_KEY)}
                />
              }
              label="Current year"
            />
            <FormControlLabel
              control={
                <Checkbox
                  value={PREVIOUS_YEAR_KEY}
                  checked={selectedYears.includes(PREVIOUS_YEAR_KEY)}
                />
              }
              label="Previous year"
            />
            <FormControlLabel
              control={
                <Checkbox
                  value={TOW_YEARS_AGO_KEY}
                  checked={selectedYears.includes(TOW_YEARS_AGO_KEY)}
                />
              }
              label="2 years ago"
            />
          </FormGroup>
        </Grid>
        <Grid item xs={12}>
          {data ? renderGraph(kpi.key, selectedYears, data) : LockedSampleGraph}
        </Grid>
      </Box>
    </Grid>
  );
};

const getBarColor = (entry: any) => {
  if (!entry) return COLOR_PRIMARY_1_800;

  if (!isNaN(entry.occupancyRate)) return COLOR_PRIMARY_1_800;

  if (entry.occupancyRate === 'inactive') {
    return {
      HO: COLOR_PRIMARY_1_400,
      LO: COLOR_GREY_200,
      C60: COLOR_GREY_200,
    }[entry.inactivityReason];
  }
};

const CustomBarGraphTooltip = ({ active, payload, activeDataKey }: TooltipProps<any, any>) => {
  const getTooltipText = entry => {
    if (!entry) return null;

    const {
      month,
      latestYear,
      occupancyRate,
      inactivityReason,
      occupancyRateWithoutLastMinuteBooking,
      daysInCurrentMonth,
    } = entry;

    const tooltipObj = {
      HO: {
        main: 'Mid-term booking or unavailable',
        learnMore: (
          <>
            In {latestYear} {month}, all 30 days were blocked, and this listing did not receive any
            review.
          </>
        ),
      },
      C60: {
        main: 'Unavailable for booking',
        learnMore: (
          <>
            In {latestYear} {month}, no night was available for booking.
          </>
        ),
      },
      LO: {
        main: 'No booking',
        learnMore: (
          <>
            In {latestYear} {month}, this listing did not receive any booking.
          </>
        ),
      },
      default: {
        occupancyRateWithoutLastMinuteBooking: {
          main: (
            <>
              {latestYear}: {occupancyRate}% occupancy (
              {Math.round((daysInCurrentMonth * occupancyRate) / 100)} out of {daysInCurrentMonth}{' '}
              nights booked)
            </>
          ),
        },
        occupancyRateFromLastMinuteBooking: {
          main: (
            <>
              {occupancyRate}% occupied by short-term bookings. Occupancy rates include a ±20%
              uncertainty due to potential last-minute bookings.
            </>
          ),
        },
      },
    };

    if (occupancyRate === 'inactive') {
      return tooltipObj[inactivityReason];
    }

    return tooltipObj['default'][activeDataKey];
  };

  if (active && payload && payload.length) {
    const tooltipText = getTooltipText(payload[0].payload);

    return (
      <Box
        sx={{
          width: 'auto',
          maxWidth: '280px',
          backgroundColor: '#ffff',
          borderRadius: '10px',
          boxShadow: '0 4px 10px #0003',
          display: 'flex',
          flexDirection: 'column',
          p: '10px',
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 'bold', color: KPI_CARD_COLOR }}>
          Occupancy Rate
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 'bold', color: KPI_CARD_COLOR, mb: '10px' }}>
          {payload[0]?.payload?.month}
        </Typography>
        <Typography variant="body2">{tooltipText['main']}</Typography>

        {tooltipText['learnMore'] && (
          <Typography variant="body2" sx={{ mt: '10px', mb: '10px', color: COLOR_TEXT_2 }}>
            ** {tooltipText['learnMore']}
          </Typography>
        )}
      </Box>
    );
  }
  return null;
};

const BarGraphComponent = ({
  data,
  BluredRevelCard,
  isDemo,
}: {
  data: any;
  BluredRevelCard: any;
  isDemo: boolean | null;
}) => {
  const [activeDataKey, setActiveDataKey] = useState<string>(
    'occupancyRateWithoutLastMinuteBooking'
  );

  if (!data) return <></>;

  const isGraphBlur = !isDemo && BluredRevelCard;

  return (
    <Grid container item xs={12} sx={{ position: 'relative' }}>
      <Box sx={{ mb: 4, width: '100%', position: 'relative' }}>
        <Grid
          container
          item
          xs={12}
          justifyContent={'space-between'}
          alignItems={'center'}
          sx={{ mt: 2 }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              marginBottom: '30px',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            Occupancy Rate Comparison
            <InfoButton
              tooltipContent={
                <InfoTooltipContent content="View the 12-month historical occupancy rate for this listing, including a detailed monthly breakdown." />
              }
            />
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            filter: isGraphBlur ? 'blur(4px) brightness(1.1)' : 'none',
            pointerEvents: isGraphBlur ? 'none' : 'auto',
          }}
        >
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="month"
                style={{ fontFamily: 'Roboto', fontSize: '12px' }}
                tickLine={false}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                style={{ fontFamily: 'Roboto', fontSize: '12px' }}
                domain={[0, 100]}
              />
              <Tooltip
                content={
                  <CustomBarGraphTooltip
                    classes
                    active
                    payload
                    label
                    activeDataKey={activeDataKey}
                  />
                }
                cursor={false}
              />
              <Bar
                dataKey={'occupancyRateWithoutLastMinuteBooking'}
                stackId={'occ'}
                isAnimationActive={true}
                animationDuration={150}
                radius={[4, 4, 4, 4]}
                barSize={13}
                style={{
                  cursor: 'pointer',
                }}
                onMouseEnter={() => setActiveDataKey('occupancyRateWithoutLastMinuteBooking')}
              >
                {data.map((entry, index) => {
                  return <Cell key={`cell-${index}`} fill={getBarColor(entry)} />;
                })}
              </Bar>

              <Bar
                dataKey={'occupancyRateFromLastMinuteBooking'}
                stackId={'occ'}
                isAnimationActive={true}
                animationDuration={150}
                radius={[4, 4, 4, 4]}
                barSize={13}
                style={{
                  cursor: 'pointer',
                }}
                onMouseEnter={() => setActiveDataKey('occupancyRateFromLastMinuteBooking')}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLOR_PRIMARY_1_400} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Grid>
      </Box>
      {isGraphBlur && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <BluredRevelCard />
        </Box>
      )}
    </Grid>
  );
};

// ------------------------- Main Component ---------------------------------------

const HistoricalPerformanceGraph: React.FC<HistoricalPerformanceGraphProps> = ({
  listingID,
  handleOnSampleClick,
  currency,
  setMarketUrlBack,
  triggerModalClose,
  isAdrAnomaly,
  showOccupancyGraphOnly,
  noTitle,
  isDemo,
  setIsDemo,
}) => {
  const user: User = useSelector((state: any) => state.dashboard.user);
  const isUserAllowed = user.subscriptionPlan === 'scale';

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IDataPoint[] | null>(null);
  const [firstAvailableMonth, setFirstAvailableMonth] = useState<string>('');

  const KPIS = [
    // { key: 'occupancy_rate', label: 'Occupancy Rate' },
    { key: 'adr', label: 'Nightly Rate' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'active_days_count', label: 'Active Days Count' },
  ];

  // -------------------- Transform API data ---------------------------
  const transformData = (inputData: any, firstAvailMonth: string): IDataPoint[] => {
    const dataKeys = Object.keys(inputData);
    const transformedData: IDataPoint[] = [];

    for (let i = dataKeys.length - 1; i >= 0; i--) {
      const key = dataKeys[i];
      const currentData = inputData[key];
      const latestYear = parseInt(key.split('-')[0]);
      const previousYear = latestYear - 1;
      const twoYearsBefore = latestYear - 2;

      const getStatus = (val: number | string | undefined, year: number) => {
        const dataDate = new Date(`${year}-${key.split('-')[1]}-01`);
        const firstAvailableDate = new Date(`${firstAvailMonth}-01`);

        if (val === 'inactive') {
          return 'inactive';
        }
        if (!val && dataDate >= firstAvailableDate) {
          return 'missing';
        }
        return dataDate < firstAvailableDate ? `undiscovered` : val;
      };

      transformedData.unshift({
        month: getMonthName(key),
        LTMData: {
          occupancy_rate: getStatus(currentData?.occupancy_rate, latestYear),
          adr: getStatus(currentData?.adr, latestYear),
          revenue: getStatus(currentData?.revenue, latestYear),
          active_days_count: getStatus(currentData?.active_days_count, latestYear),
          days_in_current_month: currentData?.days_in_current_month,
          inactivity_reason: currentData?.inactivity_reason,
        },
        last2YearsData: {
          occupancy_rate: getStatus(inputData[dataKeys[i - 12]]?.occupancy_rate, previousYear),
          adr: getStatus(inputData[dataKeys[i - 12]]?.adr, previousYear),
          revenue: getStatus(inputData[dataKeys[i - 12]]?.revenue, previousYear),
          active_days_count: getStatus(
            inputData[dataKeys[i - 12]]?.active_days_count,
            previousYear
          ),
        },
        last3YearsData: {
          occupancy_rate: getStatus(inputData[dataKeys[i - 24]]?.occupancy_rate, twoYearsBefore),
          adr: getStatus(inputData[dataKeys[i - 24]]?.adr, twoYearsBefore),
          revenue: getStatus(inputData[dataKeys[i - 24]]?.revenue, twoYearsBefore),
          active_days_count: getStatus(
            inputData[dataKeys[i - 24]]?.active_days_count,
            twoYearsBefore
          ),
        },
        latestYear,
        previousYear,
        twoYearsBefore,
        monthYear: `${getMonthName(key)},${key.split('-')[0].slice(-2)}`,
      });

      // We only show last 12 months of data
      if (transformedData.length === 12) break;
    }

    return transformedData;
  };

  // -------------------- Fetch listing history ------------------------
  const getListingHistoricalData = async (listingId: string) => {
    setLoading(true);
    const URL = 'https://8q15spida4.execute-api.us-east-2.amazonaws.com/default/getListingDetails';
    const config = {
      queryParams: {
        listingID: listingId,
        requestType: 'history',
      },
    };
    const resp: IListingHistoryResponse = await callApi(URL, config, true);
    const listingHistoricalData = resp?.message;
    const firstAvailMonth = resp?.firstAvailableMonth;

    if (listingHistoricalData) {
      const transformedData = transformData(listingHistoricalData, firstAvailMonth);
      setData(transformedData);
      setFirstAvailableMonth(firstAvailMonth);
    }

    setLoading(false);
  };

  // -------------------- Helpers for tooltips & axis ------------------
  const getKpiName = (kpi: string): string => {
    switch (kpi) {
      case 'revenue':
        return 'Revenue';
      case 'occupancy_rate':
        return 'Occupancy Rate';
      case 'adr':
        return 'Nightly Rate';
      case 'active_days_count':
        return 'Active Days Count';
      default:
        return 'Value';
    }
  };

  const formatValue = (kpi: string, value: number | string) => {
    const kpiName = getKpiName(kpi);
    if (value === 'inactive' || value === 'missing' || value === 'undiscovered') {
      if (value === 'inactive') {
        return 'Inactive';
      } else if (value === 'missing') {
        return 'Data not captured';
      } else {
        // 'undiscovered'
        return `First discovered by us on ${getMonthName(firstAvailableMonth)} ${
          firstAvailableMonth.split('-')[0]
        }`;
      }
    }
    // numeric:
    if (kpiName !== 'Occupancy Rate' && kpiName !== 'Active Days Count') {
      // currency formatting
      return isAdrAnomaly
        ? 'Unavailable due to data anomalies'
        : `${(currency && exchangeRates[currency].symbol) || ''}` +
            formatCurrency(convertCurrency(currency, +value).toString());
    } else {
      // occupancy % or active days
      return `${formatCurrency((value as number).toString())}${
        kpiName === 'Occupancy Rate' ? '%' : ''
      }`;
    }
  };

  const combineFormatterYAxis = (kpi: string, val: number): string => {
    if (kpi === 'revenue' || kpi === 'adr') {
      const converted = convertCurrency(currency, val);
      if (val > 1000) {
        return (
          `${(currency && exchangeRates[currency].symbol) || ''}` + abbreviateNumber(converted)
        );
      } else {
        return (
          `${(currency && exchangeRates[currency].symbol) || ''}` +
          abbreviateNumber(converted, false)
        );
      }
    } else if (kpi === 'occupancy_rate') {
      return val + '%';
    } else {
      // active_days_count
      return abbreviateNumber(val, false);
    }
  };

  const processOccupancyBarGraphData = (data: IDataPoint[] | null) => {
    if (!data) return [];

    const occupancyGraph: any = [];
    for (const monthObj of data) {
      const month = monthObj['month'];
      const latestYear = monthObj['latestYear'];
      const occupancyRate = monthObj['LTMData']['occupancy_rate'] as number | string;
      const activeDaysCount = monthObj['LTMData']['active_days_count'];
      const inactivityReason = monthObj['LTMData']['inactivity_reason'];
      const daysInCurrentMonth = monthObj['LTMData']['days_in_current_month'];
      const occupancyRateWithoutLastMinuteBooking = !isNaN(occupancyRate as number)
        ? Math.floor((occupancyRate as number) / 1.2)
        : null;
      const occupancyRateFromLastMinuteBooking = !isNaN(occupancyRate as number)
        ? (occupancyRate as number) - (occupancyRateWithoutLastMinuteBooking as number)
        : null;

      occupancyGraph.push({
        month,
        latestYear,
        occupancyRate,
        activeDaysCount,
        inactivityReason,
        occupancyRateWithoutLastMinuteBooking:
          occupancyRate === 'inactive' ? 100 : occupancyRateWithoutLastMinuteBooking,
        occupancyRateFromLastMinuteBooking,
        daysInCurrentMonth,
      });
    }

    return occupancyGraph;
  };

  // -------------------- Custom Tooltip component ---------------------
  const CustomTooltip = (kpi: string) => ({ active, payload }: TooltipProps<any, any>) => {
    if (active && payload && payload.length) {
      const kpiName = getKpiName(kpi);

      return (
        <Box
          sx={{
            height: '120px',
            width: 'auto',
            backgroundColor: '#ffff',
            borderRadius: '10px',
            boxShadow: '0 4px 10px #0003',
            display: 'flex',
            flexDirection: 'column',
            p: '10px',
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: KPI_CARD_COLOR }}>
            {kpiName}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: KPI_CARD_COLOR }}>
            {payload[0]?.payload?.month}
          </Typography>

          {/* LTM (Latest Year) */}
          {payload[0] && (
            <Typography variant="body1">
              <span>{payload[0]['payload']['latestYear']}</span> :{' '}
              <span style={{ color: COLOR_PRIMARY_2 }}>{formatValue(kpi, payload[0].value)}</span>
            </Typography>
          )}

          {/* last2YearsData (Previous Year) */}
          {payload[1] && (
            <Typography variant="body1">
              <span>{payload[1]['payload']['previousYear']}</span> :{' '}
              <span style={{ color: COLOR_PRIMARY_1_800 }}>
                {formatValue(kpi, payload[1].value)}
              </span>
            </Typography>
          )}

          {/* last3YearsData (Two Years Before) */}
          {payload[2] && (
            <Typography variant="body1">
              <span>{payload[2]['payload']['twoYearsBefore']}</span> :{' '}
              <span style={{ color: COLOR_PRIMARY_2_300 }}>
                {formatValue(kpi, payload[2].value)}
              </span>
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  // -------------------- Custom Legend component ----------------------
  const CustomLegend = () => {
    return (
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1, marginRight: '10px' }}>
          <Box
            sx={{ borderTop: '4px solid #037A90', width: '15px', position: 'relative', top: '5px' }}
          />
          <Typography sx={{ fontSize: '10px' }}>Last 12 Months</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, marginRight: '10px' }}>
          <Box
            sx={{
              borderTop: '2px dashed #4FA2B1',
              width: '15px',
              position: 'relative',
              top: '5px',
            }}
          />
          <Typography sx={{ fontSize: '10px' }}>Preceding Period</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, marginRight: '10px' }}>
          <Box
            sx={{
              borderTop: `2px dashed ${COLOR_PRIMARY_1_800}`,
              width: '15px',
              position: 'relative',
              top: '5px',
            }}
          />
          <Typography sx={{ fontSize: '10px' }}>Previous Year</Typography>
        </Box>
      </Box>
    );
  };

  // -------------------- Render single KPI chart ----------------------
  const renderGraph = (kpi: string, selectedYears: string[], data: IDataPoint[]) => {
    return (
      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={data as any} margin={{ top: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="month"
            style={{ fontFamily: 'Roboto', fontSize: '12px' }}
            tickLine={false}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            style={{ fontFamily: 'Roboto', fontSize: '12px' }}
            tickFormatter={(val: number) => combineFormatterYAxis(kpi, val)}
            domain={kpi === 'occupancy_rate' ? [0, 100] : undefined}
          />
          <Tooltip content={CustomTooltip(kpi)} />
          <Legend content={<CustomLegend />} />
          {selectedYears.includes(CURRENT_YEAR_KEY) && (
            <Line
              type="monotone"
              dataKey={`LTMData.${kpi}`}
              stroke={COLOR_PRIMARY_2}
              strokeWidth={3}
            />
          )}
          {selectedYears.includes(PREVIOUS_YEAR_KEY) && (
            <Line
              type="monotone"
              dataKey={`last2YearsData.${kpi}`}
              stroke={COLOR_PRIMARY_1_800}
              strokeDasharray="5 5"
            />
          )}
          {selectedYears.includes(TOW_YEARS_AGO_KEY) && (
            <Line
              type="monotone"
              dataKey={`last3YearsData.${kpi}`}
              stroke={COLOR_PRIMARY_2_300}
              strokeDasharray="5 5"
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  // -------------------- Locked sample chart (blurred) ----------------

  const BluredRevelCard = () => {
    return (
      <>
        {/* Centered Clickable CTA */}
        {loading ? (
          <CircularProgress />
        ) : (
          <Typography
            style={styles.revealButton}
          >
            <span
              style={{ textDecoration: 'underline' }}
              onClick={() => {
                if (isUserAllowed) {
                  getListingHistoricalData(listingID);
                } else {
                  setMarketUrlBack();
                  triggerModalClose();
                  handleOnSampleClick('scale');
                }
              }}
            >
              {isUserAllowed ? 'Reveal' : SCALE_CTA}
            </span>
            {['pro', 'expert'].includes(user.subscriptionPlan) && (
              <>
                <br />
                <span style={{ color: '#000' }}>OR</span>
                <br />
                <span onClick={() => setIsDemo(true)} style={{ textDecoration: 'underline' }}>
                  View Demo
                </span>
              </>
            )}
          </Typography>
        )}
      </>
    );
  };

  const LockedSampleGraph = (
    <Grid
      container
      item
      xs={12}
      sx={{
        position: 'relative',
        padding: '10px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '300px',
      }}
    >
      {/* Blurred overlay to simulate a locked state */}
      <div
        style={{
          height: '100%',
          position: 'absolute',
          top: '0',
          left: '0',
          width: '100%',
          background: '#ffffff',
          filter: 'blur(6px)',
          opacity: 0.8,
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            height: '250px',
            width: '100%',
            marginLeft: '-50px',
            marginTop: '10px',
          }}
        >
          <ResponsiveContainer width="100%" height={250}>
            <LineChart width={500} height={250} data={dummyData}>
              <XAxis dataKey="name" tick={false} />
              <YAxis tick={false} />
              <Line type="monotone" dataKey="pv" stroke="#000" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </div>

      <BluredRevelCard />
    </Grid>
  );

  return (
    <>
      {!noTitle && (
        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
          How has this listing performed historically?
        </Typography>
      )}

      {!showOccupancyGraphOnly && (
        <Grid container>
          {KPIS.map(kpi => (
            <KPIGraphComponent
              key={'kpi-session-graph-' + kpi.key}
              kpi={kpi}
              renderGraph={renderGraph}
              data={isDemo ? SAMPLE_LISTING_HISTORICAL_DATA : data}
              LockedSampleGraph={LockedSampleGraph}
            />
          ))}
        </Grid>
      )}

      {showOccupancyGraphOnly && (
        <Grid container>
          <BarGraphComponent
            data={processOccupancyBarGraphData(data ? data : SAMPLE_LISTING_HISTORICAL_DATA)}
            BluredRevelCard={data ? false : BluredRevelCard}
            isDemo={isDemo}
          />
        </Grid>
      )}

      {!showOccupancyGraphOnly && (
        <Typography variant="caption" sx={{ mt: 1, fontSize: '14px' }}>
          <span style={{ verticalAlign: 'middle' }}>
            Missing or inactive data?{' '}
            <span
              style={{
                color: COLOR_PRIMARY_2,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
              onClick={() => window.open('https://airbtics.com/support/', '_blank')}
            >
              Learn why
            </span>
          </span>
        </Typography>
      )}
    </>
  );
};

export default HistoricalPerformanceGraph;
